import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Typography, Space, Tag, Card, Progress, Row, Col } from "antd";
import { MetricComparison } from "../components/MetricComparison";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Tags } from "../components/Tags";

interface GroupData {
  metric: string;
  metric_unit: string;
  group_name: string;
  previous_value: number;
  current_value: number;
  delta: number;
  percentage_change: number;
  commentary: string;
  related_factors: string[];
  sample_size?: number;
}

// GroupAnalysis Block Recipe
const groupAnalysisRecipe = {
  id: "group-analysis-block",
  name: "Group Analysis Block",
  globalKey: [],
  prefix: `[
        {
          "metric": "string",
          "metric_unit": "string",
          "group_name": "string",
          "previous_value": 0,
          "current_value": 0,
          "delta": 0,
          "percentage_change": 0,
          "commentary": "string",
          "related_factors": ["string"]
          "sample_size": "number — optional"
        }
    ]`,
  format: {
    type: "json",
    fields: ["metric", "group_name", "current_value", "previous_value"],
    constraints: [
      { field: "metric", type: "string", required: true },
      { field: "group_name", type: "string", required: true },
      { field: "current_value", type: "number", required: true },
      { field: "previous_value", type: "number", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as GroupData[];
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (item) =>
                item.metric &&
                item.group_name &&
                typeof item.current_value === "number" &&
                typeof item.previous_value === "number"
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// GroupAnalysis Block Parser
function groupAnalysisParser(data: any) {
  try {
    return {
      data: data[groupAnalysisBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse group analysis data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// GroupAnalysis Block Variants
const groupAnalysisVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard group analysis display with metrics and comparisons",
    defaultSettings: {
      layout: "grid",
      showMetrics: true,
      showFactors: true,
      showCommentary: true,
    },
  },
  {
    id: "comparison",
    name: "Comparison",
    description: "Focused comparison view with emphasis on changes",
    defaultSettings: {
      layout: "comparison",
      showMetrics: true,
      showFactors: false,
      showCommentary: true,
    },
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal display with essential information",
    defaultSettings: {
      layout: "minimal",
      showMetrics: true,
      showFactors: false,
      showCommentary: false,
      showPill: false,
    },
  },
  {
    id: "radar",
    name: "Radar",
    description: "Creative radar view showing relative performance",
    defaultSettings: {
      layout: "radar",
      showMetrics: true,
      showFactors: true,
      showCommentary: true,
      showPill: false,
    },
  },
];

// Default View Renderer
const DefaultViewRenderer: React.FC<{ group: GroupData; settings: any }> = ({
  group,
  settings,
}) => (
  <Space direction="vertical" size="small" style={{ width: "100%" }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        justifyContent: "space-between",
      }}
    >
      <Typography.Text className="vzbl-header-5">
        {stripMarkdown(group.group_name)}
      </Typography.Text>
      {settings?.showPill && group.sample_size !== undefined && (
        <Tag
          className="vzbl-pill-tag"
          color="var(--color-pill-blue)"
          style={{
            color: "var(--color-pill-blue-accent)",
            borderColor: "var(--color-pill-blue-accent)",
          }}
        >{`Sample Size: ${group.sample_size}`}</Tag>
      )}
    </div>
    <MetricComparison
      variant="default"
      previousValue={group.previous_value}
      currentValue={group.current_value}
      changeValue={group.percentage_change}
      unit={group.metric_unit}
      className="vzbl-spacing-small"
    />
    {settings?.showCommentary && group.commentary && (
      <Markdown
        remarkPlugins={[remarkGfm]}
        skipHtml={true}
        className="vzbl-text-3 vzbl-spacing-small"
      >
        {group.commentary}
      </Markdown>
    )}
    {settings?.showFactors && group.related_factors?.length > 0 && (
      <Space wrap>
        {group.related_factors.map((f: string) => (
          <Tag
            key={`group-tag-${f}`}
            className="vzbl-pill-tag vzbl-spacing-small"
          >
            {stripMarkdown(f)}
          </Tag>
        ))}
      </Space>
    )}
  </Space>
);

// Comparison View Renderer
const ComparisonViewRenderer: React.FC<{ group: GroupData; settings: any }> = ({
  group,
  settings,
}) => (
  <Space direction="vertical" size="small" style={{ width: "100%" }}>
    <Typography.Text className="vzbl-header-5">
      {stripMarkdown(group.group_name)}
    </Typography.Text>
    <Row gutter={8} align="middle" style={{ width: "100%" }}>
      <Col span={8} style={{ textAlign: "left" }}>
        <Typography.Text type="secondary" className="vzbl-text-3">
          Previous
        </Typography.Text>
        <div className="vzbl-text-3">
          {group.previous_value} {group.metric_unit}
        </div>
      </Col>
      <Col span={8} style={{ textAlign: "center" }}>
        <Typography.Text type="secondary" className="vzbl-text-3">
          Change
        </Typography.Text>
        <div
          className="vzbl-text-3"
          style={{
            color:
              group.percentage_change > 0
                ? "var(--color-success)"
                : "var(--color-error)",
          }}
        >
          {group.percentage_change > 0 ? "+" : ""}
          {group.percentage_change}%
        </div>
      </Col>
      <Col span={8} style={{ textAlign: "right" }}>
        <Typography.Text type="secondary" className="vzbl-text-3">
          Current
        </Typography.Text>
        <div className="vzbl-text-3">
          {group.current_value} {group.metric_unit}
        </div>
      </Col>
    </Row>
    {settings?.showCommentary && group.commentary && (
      <Markdown
        remarkPlugins={[remarkGfm]}
        skipHtml={true}
        className="vzbl-text-3 vzbl-spacing-small"
      >
        {group.commentary}
      </Markdown>
    )}
  </Space>
);

// Minimal View Renderer
const MinimalStatsViewRenderer: React.FC<{
  group: GroupData;
  settings: any;
}> = ({ group, settings }) => (
  <Space direction="vertical" size="small" style={{ width: "100%" }}>
    <Typography.Text className="vzbl-header-5">
      {stripMarkdown(group.group_name)}
    </Typography.Text>
    <MetricComparison
      variant="minimal"
      previousValue={group.previous_value}
      currentValue={group.current_value}
      changeValue={group.percentage_change}
      unit={group.metric_unit}
    />
  </Space>
);

// Radar View Renderer
const RadarViewRenderer: React.FC<{
  group: GroupData;
  settings: any;
  totalValue: number;
}> = ({ group, settings, totalValue }) => {
  const progressPercent =
    totalValue > 0 ? (group.current_value / totalValue) * 100 : 0;

  return (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Typography.Text className="vzbl-header-5">
        {stripMarkdown(group.group_name)}
      </Typography.Text>
      <div style={{ width: "65%" }}>
        <Progress
          percent={progressPercent}
          showInfo={true}
          strokeColor={{
            from: "var(--color-blue-1)",
            to: "var(--color-blue-2)",
            direction: "to right",
          }}
          format={(percent) => (
            <Space>
              <span>
                {group.current_value} {group.metric_unit}
              </span>
              {group.percentage_change !== 0 && (
                <Typography.Text>
                  {group.percentage_change > 0 ? (
                    <ArrowUpOutlined />
                  ) : (
                    <ArrowDownOutlined />
                  )}{" "}
                  {Math.abs(group.percentage_change)}%
                </Typography.Text>
              )}
            </Space>
          )}
        />
      </div>
      {settings?.showCommentary && group.commentary && (
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3 vzbl-spacing-small"
        >
          {group.commentary}
        </Markdown>
      )}
      {settings?.showFactors && group.related_factors?.length > 0 && (
        <Tags
          key={`group-tag-${group.group_name}`}
          tags={group.related_factors}
        />
      )}
    </Space>
  );
};

// GroupAnalysis Block Renderer
function GroupAnalysisBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const groupsData = data[groupAnalysisBlock.id];
  const variantSettings = groupAnalysisVariants.find(
    (v) => v.id === variant
  )?.defaultSettings;

  if (!groupsData?.length) return null;

  // Calculate total value for radar view
  const totalValue = groupsData.reduce(
    (sum: number, group: GroupData) => sum + group.current_value,
    0
  );

  const renderers = {
    default: DefaultViewRenderer,
    comparison: ComparisonViewRenderer,
    minimalStats: MinimalStatsViewRenderer,
    radar: RadarViewRenderer,
  };

  const SelectedRenderer =
    renderers[variant as keyof typeof renderers] || DefaultViewRenderer;

  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-5">
        Group Analysis
      </Typography.Title>
      <div className="responsive-card-grid">
        {groupsData.map((group: GroupData, idx: number) => (
          <div
            key={`group-${idx}`}
            style={{ height: "100%", width: "100%" }}
            className="vzbl-panel-shadow vzbl-panel-overlay"
          >
            <SelectedRenderer
              group={group}
              settings={variantSettings}
              totalValue={variant === "radar" ? totalValue : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Create GroupAnalysis Block
const groupAnalysisBlock = createBaseBlock({
  id: "group-analysis",
  name: "Group Analysis Block",
  description: "Displays category analysis with metrics and trends",
  recipe: groupAnalysisRecipe,
  variants: groupAnalysisVariants,
  parser: groupAnalysisParser,
  renderer: GroupAnalysisBlockRenderer,
});

// Export the block for direct use
export { groupAnalysisBlock };
